import type { Broadcast, BroadcastStatus } from '../types/broadcast';

// Stage 6A v7 — pure filter + sort logic for the broadcast list. Kept out of the
// (untested) page/table components so the null-sentAt edge + toggle behaviour are
// unit-testable.

export type BroadcastStatusFilter = BroadcastStatus | 'all';

export type BroadcastSortKey = 'title' | 'brand' | 'status' | 'recipients' | 'sentAt';
export type SortDir = 'asc' | 'desc';
export type BroadcastSort = { key: BroadcastSortKey; dir: SortDir };

// Default: most-recently-SENT first. Drafts (null sentAt) cluster at the bottom.
export const DEFAULT_BROADCAST_SORT: BroadcastSort = { key: 'sentAt', dir: 'desc' };

// Dropdown options — driven by the real BroadcastStatus enum (no cancelled /
// in_progress exist). 'all' is the default.
export const BROADCAST_STATUS_FILTER_OPTIONS: Array<{
  value: BroadcastStatusFilter;
  label: string;
}> = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'sending', label: 'Sending' },
  { value: 'sent', label: 'Sent' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

// Lifecycle order, so a status sort groups logically rather than alphabetically.
const STATUS_RANK: Record<BroadcastStatus, number> = {
  draft: 0,
  sending: 1,
  sent: 2,
  completed: 3,
  failed: 4,
};

// Columns whose natural first click is descending (most/newest first); everything
// else defaults to ascending (A→Z).
const DESC_FIRST: ReadonlySet<BroadcastSortKey> = new Set(['sentAt', 'recipients']);

export function defaultSortDir(key: BroadcastSortKey): SortDir {
  return DESC_FIRST.has(key) ? 'desc' : 'asc';
}

// Header-click reducer: clicking the active column flips direction; clicking a
// new column switches to it at its natural default direction.
export function nextSort(current: BroadcastSort, key: BroadcastSortKey): BroadcastSort {
  if (current.key === key) {
    return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { key, dir: defaultSortDir(key) };
}

export function filterBroadcastsByStatus(
  items: Broadcast[],
  status: BroadcastStatusFilter,
): Broadcast[] {
  if (status === 'all') return items;
  return items.filter((b) => b.status === status);
}

function ms(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

// sentAt comparator with NULLS ALWAYS LAST (in both directions), so drafts never
// scatter through the list — they cluster at the bottom. Two drafts tie-break by
// createdAt desc (newest draft first) for a stable, predictable cluster.
function compareSentAt(a: Broadcast, b: Broadcast, dir: SortDir): number {
  const am = ms(a.sentAt);
  const bm = ms(b.sentAt);
  if (am === null && bm === null) {
    return (ms(b.createdAt) ?? 0) - (ms(a.createdAt) ?? 0);
  }
  if (am === null) return 1; // a (draft) after b
  if (bm === null) return -1; // b (draft) after a
  return dir === 'asc' ? am - bm : bm - am;
}

// Sort a copy of `items`. `brandLabel` resolves the brand-column display value
// for the brand key (defaults to '' so the helper is usable without it).
export function sortBroadcasts(
  items: Broadcast[],
  sort: BroadcastSort,
  brandLabel: (b: Broadcast) => string = () => '',
): Broadcast[] {
  const dir = sort.dir;
  const oriented = (c: number) => (dir === 'asc' ? c : -c);
  const copy = [...items];
  copy.sort((a, b) => {
    let c = 0;
    switch (sort.key) {
      case 'sentAt':
        c = compareSentAt(a, b, dir);
        break;
      case 'title':
        c = oriented(a.title.localeCompare(b.title));
        break;
      case 'brand':
        c = oriented(brandLabel(a).localeCompare(brandLabel(b)));
        break;
      case 'recipients':
        c = oriented(a.totalRecipients - b.totalRecipients);
        break;
      case 'status':
        c = oriented(STATUS_RANK[a.status] - STATUS_RANK[b.status]);
        break;
    }
    if (c !== 0) return c;
    // Deterministic tie-break: most-recent-sent first, drafts last.
    return compareSentAt(a, b, 'desc');
  });
  return copy;
}
